import { v7 as uuidv7 } from "uuid";
import type { PhoneScript } from "@/types/campaigns";
import type { UseCase, UseCaseAction, UseCaseAssociation } from "@/types/scripts";

export type EmbeddedUseCasePayload = {
  order: number;
  status: "active" | "inactive";
  title: string;
  description: string;
  actions: UseCaseAction[];
};

const stripActionClientFields = (action: UseCaseAction): UseCaseAction => {
  const { _key: _unusedKey, ...rest } = action;
  return rest;
};

function toUiUseCaseStatus(status: string | undefined): UseCase["status"] {
  return status === "archived" || status === "inactive" ? "archived" : "active";
}

function toEmbeddedUseCaseStatus(status: string | undefined): EmbeddedUseCasePayload["status"] {
  return status === "archived" || status === "inactive" ? "inactive" : "active";
}

export function hasOwnedUseCases(
  script: PhoneScript | null | undefined
): script is PhoneScript & { useCases: UseCase[] } {
  return (script?.useCases?.length ?? 0) > 0;
}

export function shouldSkipUseCaseBatchFetch(
  isV2: boolean,
  _script?: PhoneScript | null
): boolean {
  return isV2;
}

function overlayMap(
  modifiedUseCases?: Map<string, UseCase> | Record<string, UseCase>
): Map<string, UseCase> {
  if (!modifiedUseCases) {
    return new Map();
  }
  return modifiedUseCases instanceof Map
    ? modifiedUseCases
    : new Map(Object.entries(modifiedUseCases));
}

/**
 * Key the owned use cases Galactus returned by their `order`, which it keeps
 * unique within a script. Rows the editor added before saving keep their key.
 */
export function keyOwnedUseCasesByOrder<T extends PhoneScript | null | undefined>(script: T): T {
  if (!script?.useCases?.length) return script;
  if (script.useCases.every((useCase) => useCase.id)) return script;
  return {
    ...script,
    useCases: script.useCases.map((useCase, index) => {
      if (useCase.id) return useCase;
      const hasOrder = "order" in useCase && typeof useCase.order === "number";
      return { ...useCase, id: hasOrder ? String(useCase.order) : `idx_${index + 1}` };
    }),
  };
}

/**
 * Flattened editor rows for v2: `useCases[]` in list order, with in-memory
 * edits overlaid. Never reads `useCaseAssociations`.
 */
export function flattenOwnedUseCases(
  script: PhoneScript | null | undefined,
  modifiedUseCases?: Map<string, UseCase> | Record<string, UseCase>
): UseCase[] {
  const overlay = overlayMap(modifiedUseCases);
  return (script?.useCases ?? []).map((useCase) => {
    const edited = overlay.get(useCase.id);
    if (!edited) {
      return useCase;
    }
    // Overlay is content edits; the script-owned row is the source of truth
    // for enabled/archived so a stale modified-map status cannot re-enable
    // an action the editor just toggled off.
    return { ...edited, status: useCase.status };
  });
}

export function hydrateUseCasesFromScript(script: PhoneScript | null | undefined): UseCase[] {
  return (script?.useCases ?? []).map((useCase) => ({
    id: useCase.id,
    title: useCase.title,
    description: useCase.description,
    actions: (useCase.actions ?? []).map((action) => ({ ...action })),
    status: toUiUseCaseStatus(useCase.status),
    ...(useCase.textScriptId ? { textScriptId: useCase.textScriptId } : {}),
    ...(useCase.type ? { type: useCase.type } : {}),
  }));
}

/**
 * Active use cases a Try call runs with. A v2 script owns its use cases and carries
 * no associations; a v1 script links library use cases through its associations.
 * Decided by API version, not by `useCases` being present: v1 save bodies and editor
 * state also carry `useCases: []`.
 */
export function resolveTryUseCases(
  script: PhoneScript,
  libraryUseCases: UseCase[],
  isV2: boolean
): UseCase[] {
  if (isV2) {
    return hydrateUseCasesFromScript(script).filter((useCase) => useCase.status === "active");
  }
  return (script.useCaseAssociations ?? [])
    .filter((association) => association.status === "active")
    .map((association) => libraryUseCases.find((useCase) => useCase.id === association.id))
    .filter((useCase): useCase is UseCase => !!useCase);
}

export function associationsFromEmbeddedUseCases(
  useCases: UseCase[]
): UseCaseAssociation[] {
  return useCases.map((useCase, index) => ({
    id: useCase.id,
    order: index + 1,
    status: toEmbeddedUseCaseStatus(useCase.status),
  }));
}

export function ownedHydrateKey(
  isV2: boolean,
  scriptId: string | undefined,
  useCases: UseCase[] | undefined
): string {
  if (!isV2) {
    return "";
  }
  // Order keys repeat across saves, so the text must be in the key for a save response to rehydrate.
  const ids = (useCases ?? []).map((useCase) => useCase.id).join("|");
  return `${scriptId ?? ""}:${ids}\n${serializeEmbeddedUseCases(useCases)}`;
}

export function editorAssociations(
  script: PhoneScript,
  isV2: boolean
): UseCaseAssociation[] {
  if (isV2) {
    return associationsFromEmbeddedUseCases(script.useCases ?? []);
  }
  return script.useCaseAssociations ?? [];
}

/**
 * Owned `useCases[]` for Galactus v2 create/update, in UI list order.
 * Greeting / goodbye / voicemail are not included.
 */
export function buildEmbeddedUseCasesPayload(
  script: PhoneScript,
  modifiedUseCases?: Map<string, UseCase>
): EmbeddedUseCasePayload[] {
  return flattenOwnedUseCases(script, modifiedUseCases).map((useCase, index) => ({
    order: index + 1,
    status: toEmbeddedUseCaseStatus(useCase.status),
    title: useCase.title ?? "",
    description: useCase.description ?? "",
    actions: (useCase.actions ?? []).map(stripActionClientFields),
  }));
}

/** A v2 script save body: owned use cases in wire shape and no associations. */
export type EmbeddedUseCasesBody<T extends PhoneScript> = Omit<
  T,
  "useCases" | "useCaseAssociations"
> & {
  useCases: EmbeddedUseCasePayload[];
  useCaseAssociations: [];
};

/** `body` as a v2 save body, with owned use cases read from `source`. */
export function withEmbeddedUseCasesBody<T extends PhoneScript>(
  body: T,
  source: PhoneScript,
  modifiedUseCases?: Map<string, UseCase>
): EmbeddedUseCasesBody<T> {
  const { useCases: _useCases, useCaseAssociations: _associations, ...rest } = body;
  return {
    ...rest,
    useCases: buildEmbeddedUseCasesPayload(source, modifiedUseCases),
    useCaseAssociations: [],
  };
}

export function cloneUseCaseForImport(useCase: UseCase): UseCase {
  return {
    ...useCase,
    id: uuidv7(),
    actions: (useCase.actions ?? []).map((action) => ({
      ...action,
      _key: undefined,
    })),
  };
}

export function serializeEmbeddedUseCases(useCases: UseCase[] | undefined): string {
  if (!useCases?.length) {
    return "";
  }

  return useCases
    .map((useCase, index) => {
      const actionText = (useCase.actions ?? [])
        .map((action) => action.description)
        .filter(Boolean)
        .join(" | ");
      const body = [useCase.title, useCase.description, actionText].filter(Boolean).join(" — ");
      return `${index + 1}. ${body}`;
    })
    .join("\n");
}
